import {
  createNotice,
  useCreateViolation,
  useDeleteViolation,
  useListViolations,
  useUpdateViolation,
  type ViolationIn,
  type ViolationOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, FormControlLabel, Stack, Switch } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
  optional,
} from '../../components/RecordPage';
import { listResult } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { optionsOf } from '../maintenance/labels';
import { RecordExtras } from '../records/RecordExtras';
import { VIOLATION_CATEGORY, VIOLATION_STATUS } from './labels';
import { useLeaseOptions } from './useLeaseOptions';

const COLUMNS: Column<ViolationOut>[] = [
  { label: 'Rental', render: (v) => v.place },
  { label: 'Residents', render: (v) => v.residents || '–' },
  { label: 'Type', render: (v) => VIOLATION_CATEGORY[v.category] },
  { label: 'Happened', render: (v) => formatDate(v.occurred_on) },
  {
    label: 'Cure By',
    render: (v) =>
      v.cure_by ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{formatDate(v.cure_by)}</span>
          {v.past_cure && <Chip size="small" color="error" label="Past Due" />}
        </Stack>
      ) : (
        '–'
      ),
  },
  {
    label: 'Status',
    render: (v) => (
      <Chip
        size="small"
        color={VIOLATION_STATUS[v.status].color}
        label={VIOLATION_STATUS[v.status].label}
      />
    ),
  },
];

function NoticeFromViolation({ violation }: { violation: ViolationOut }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function draft() {
    setError(null);
    try {
      await createNotice({
        lease_id: violation.lease_id,
        kind: 'violation',
        violation_id: violation.id,
        respond_by: violation.cure_by,
      });
      await queryClient.invalidateQueries();
      setMessage('A draft is waiting under Notices.');
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }
  return (
    <Stack spacing={1} sx={{ mb: 3, alignItems: 'flex-start' }}>
      {message && <Alert severity="success">{message}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      <Button size="small" variant="outlined" onClick={() => void draft()}>
        Draft a Violation Notice
      </Button>
    </Stack>
  );
}

function toBody(values: FormValues): ViolationIn {
  return {
    lease_id: Number(required(values, 'lease_id')),
    category: required(values, 'category') as ViolationIn['category'],
    description: required(values, 'description'),
    occurred_on: required(values, 'occurred_on'),
    cure_by: optional(values, 'cure_by'),
    status: required(values, 'status') as ViolationIn['status'],
  };
}

export function ViolationsPage() {
  const canManage = useCan('notices.manage');
  const create = useCreateViolation();
  const update = useUpdateViolation();
  const remove = useDeleteViolation();
  const leases = useLeaseOptions();
  const [openOnly, setOpenOnly] = useState(true);
  const useRows = ({ q, page, pageSize }: ListParams) =>
    listResult(useListViolations({ q, page, page_size: pageSize, open_only: openOnly }));
  const fields: Field[] = [
    { name: 'lease_id', label: 'Lease', type: 'select', required: true, options: leases },
    {
      name: 'category',
      label: 'Type',
      type: 'select',
      required: true,
      defaultValue: 'other',
      options: optionsOf(VIOLATION_CATEGORY),
    },
    { name: 'description', label: 'What Happened', type: 'multiline', required: true },
    { name: 'occurred_on', label: 'Date It Happened', type: 'date', required: true },
    { name: 'cure_by', label: 'Must Be Fixed By', type: 'date' },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'open',
      options: optionsOf(VIOLATION_STATUS),
    },
  ];
  return (
    <RecordPage
      title="Lease Violations"
      singular="violation"
      description="Staff only, until you send a notice. Add photos under Files & Notes."
      summary={
        <FormControlLabel
          label="Open Violations Only"
          control={<Switch checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />}
        />
      }
      fields={fields}
      columns={COLUMNS}
      canManage={canManage}
      useRows={useRows}
      toValues={(v) => ({
        lease_id: String(v.lease_id),
        category: v.category,
        description: v.description,
        occurred_on: v.occurred_on,
        cure_by: v.cure_by ?? '',
        status: v.status,
      })}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) =>
        update.mutateAsync({ violationId: recordId, data: toBody(values) })
      }
      remove={(recordId) => remove.mutateAsync({ violationId: recordId })}
      renderExtras={(row) => (
        <>
          {canManage && <NoticeFromViolation violation={row} />}
          <RecordExtras entityType="violation" entityId={row.id} canManage={canManage} />
        </>
      )}
    />
  );
}
